import { Link } from 'react-router-dom';
import { Box, Clock, MapPin, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from './StatusBadge';
import { DevicePhoto } from './DevicePhoto';
import { TelemetryIcons } from './TelemetryIcons';
import {
  computeOperationalStatus,
  getDeviceNickname,
  isBlocked,
  readTelemetry,
} from '@/lib/machineStatus';
import { BlockedBadge } from './BlockedBadge';
import { formatRelativeTime, formatDateTime, cn } from '@/lib/utils';
import { formatAddress } from '@/lib/address';
import type { Device, Position } from '@/api/types';

interface Props {
  device: Device;
  position?: Position;
}

export function DeviceCard({ device, position }: Props) {
  const status = computeOperationalStatus(device, position);
  const blocked = isBlocked(device, position);
  const telemetry = readTelemetry(position);
  const nickname = getDeviceNickname(device);
  const lastUpdate = position?.fixTime ?? device.lastUpdate;
  const address = formatAddress(position?.address);
  const hasTelemetry =
    telemetry.ignition !== undefined ||
    telemetry.batteryLevel !== undefined ||
    telemetry.battery !== undefined ||
    telemetry.blocked ||
    telemetry.alarm ||
    telemetry.charging;

  return (
    <Link to={`/devices/${device.id}`} className="group block">
      <Card
        className={cn(
          'h-full transition-all hover:border-primary/40 hover:shadow-md',
          'group-focus-visible:ring-2 group-focus-visible:ring-ring',
        )}
      >
        <CardContent className="flex h-full flex-col gap-3 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3
                className="truncate text-base font-bold tracking-tight"
                title={device.name}
              >
                {device.name}
              </h3>
              {nickname ? (
                <p
                  className="mt-0.5 flex items-center gap-1 truncate text-xs font-medium text-foreground/70"
                  title={`Apelido: ${nickname}`}
                >
                  <Pencil className="h-3 w-3 shrink-0 text-muted-foreground" />
                  {nickname}
                </p>
              ) : (
                <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                  <Pencil className="h-3 w-3 shrink-0" />
                  Adicionar apelido
                </p>
              )}
            </div>
            {/* Coluna própria para os selos: com dois deles, sem quebra de linha o nome da
                embarcação era espremido em tela de celular. */}
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
              <StatusBadge status={status} />
              {blocked && <BlockedBadge blocked />}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <DevicePhoto device={device} size="lg" />
            <div className="min-w-0 flex-1 space-y-1.5 text-xs">
              {device.model?.trim() && (
                <div className="flex items-center gap-1.5">
                  <Box className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate font-semibold" title={device.model}>
                    {device.model}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate font-semibold">{formatDateTime(lastUpdate)}</span>
              </div>
              <p className="text-muted-foreground">{formatRelativeTime(lastUpdate)}</p>
            </div>
          </div>

          <div className="flex items-start gap-1.5 text-xs">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="line-clamp-2 text-foreground/80" title={address}>
              {address || 'Sem endereço'}
            </span>
          </div>

          {hasTelemetry && (
            <div className="border-t border-border pt-2">
              <TelemetryIcons telemetry={telemetry} />
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
